import React from "react";
import { Link } from "react-router-dom";
import markUrl from "./goollooper-mark.svg";

/**
 * Goollooper website — goollooper.com (apex + www).
 * Rebuilt 2026-11-04 on the goollooper-website branch at Uche's direction:
 * the legacy site content was deleted wholesale and recreated in the image of
 * the platform's three technologies — Ixee, Ixra and Whixpar.
 * Copy: drafted by Dotty 2026-11-04, APPROVED by Uche 2026-11-04 ("I love it! Approved.").
 * Header lockup added 2026-11-04 at Uche's direction: the Goollooper loop mark
 * (vector source: goollooper-admin-frontend Logo.svg; brand copies in
 * ~/workspace/goollooper/brand/) beside the typeset wordmark.
 * Design language matches ixee.ai: ink #161A1D, Space Grotesk display, Inter body.
 */

const APP_STORE_URL = "https://apps.apple.com/us/app/goollooper/id6458144675";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.apps.goollooper";
const WEB_APP_URL = "https://app.ixee.ai";

const Wordmark = ({ className = "text-2xl", markClassName = "h-9 w-9" }) => (
  <span className="inline-flex items-center gap-2.5">
    <img src={markUrl} alt="" aria-hidden="true" className={markClassName} />
    <span
      className={`font-bold tracking-wide text-[#161A1D] ${className}`}
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      GOOLLOOPER
    </span>
  </span>
);

export const GoollooperNav = () => (
  <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-neutral-200">
    <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
      <Link to="/" aria-label="Goollooper home">
        <Wordmark />
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600">
        <a href="#app" className="hover:text-[#161A1D]">The App</a>
        <a href="#ixee" className="hover:text-[#161A1D]">Ixee</a>
        <a href="#ixra" className="hover:text-[#161A1D]">Ixra</a>
        <a href="#whixpar" className="hover:text-[#161A1D]">Whixpar</a>
        <a href="#megablast" className="hover:text-[#161A1D]">Mega Blast</a>
      </nav>
      <a
        href={APP_STORE_URL}
        className="bg-[#161A1D] text-white text-sm font-medium px-4 py-2 rounded-full hover:bg-black"
      >
        Get the app
      </a>
    </div>
  </header>
);

export const GoollooperFooter = () => (
  <footer className="border-t border-neutral-200 mt-24">
    <div className="max-w-6xl mx-auto px-5 py-10 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
      <div>
        <Wordmark className="text-xl" markClassName="h-7 w-7" />
        <p className="text-sm text-neutral-500 mt-1">
          Productivity with every connection.
        </p>
      </div>
      <nav className="flex gap-6 text-sm text-neutral-600">
        <Link to="/privacy" className="hover:text-[#161A1D]">Privacy</Link>
        <Link to="/terms" className="hover:text-[#161A1D]">Terms</Link>
        <Link to="/delete-account" className="hover:text-[#161A1D]">Delete account</Link>
      </nav>
    </div>
    <div className="max-w-6xl mx-auto px-5 pb-8 text-xs text-neutral-400">
      © 2026 Goollooper Inc. Support lives inside the app — just ask Ixee.
    </div>
  </footer>
);

const SectionTitle = ({ eyebrow, title, children }) => (
  <div className="max-w-2xl">
    <p className="text-xs font-semibold tracking-[0.2em] text-neutral-500 uppercase">{eyebrow}</p>
    <h2
      className="text-3xl md:text-4xl font-bold text-[#161A1D] mt-3"
      style={{ fontFamily: "'Space Grotesk', sans-serif" }}
    >
      {title}
    </h2>
    {children && <p className="text-neutral-600 mt-4 leading-relaxed">{children}</p>}
  </div>
);

const StoreButtons = ({ center = false }) => (
  <div className={`flex flex-wrap items-center gap-4 ${center ? "justify-center" : ""}`}>
    <a
      href={APP_STORE_URL}
      className="bg-[#161A1D] text-white font-medium px-6 py-3 rounded-full hover:bg-black"
    >
      Download on the App Store
    </a>
    <a
      href={PLAY_STORE_URL}
      className="border border-neutral-300 text-[#161A1D] font-medium px-6 py-3 rounded-full hover:border-neutral-500"
    >
      Get it on Google Play
    </a>
  </div>
);

const GoollooperHome = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
    <GoollooperNav />
    <main>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 pt-20 pb-16 text-center">
        <p className="text-xs font-semibold tracking-[0.25em] text-neutral-500 uppercase">
          Goollooper Inc.
        </p>
        <h1
          className="text-5xl md:text-6xl font-bold text-[#161A1D] mt-5 leading-tight"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Productivity with every connection.
        </h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto mt-6 leading-relaxed">
          Goollooper is a social productivity platform — one app where people find,
          hire and help each other — powered by three technologies we are perfecting:
          Ixee, Ixra and Whixpar.
        </p>
        <div className="mt-9">
          <StoreButtons center />
        </div>
        <p className="text-sm text-neutral-400 mt-5">
          Live on iOS and Android. The web app lives at{" "}
          <a href={WEB_APP_URL} className="underline hover:text-[#161A1D]">app.ixee.ai</a>.
        </p>
      </section>

      {/* The App */}
      <section id="app" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="The App" title="One app for getting things done">
          Describe what you need and Goollooper matches you with the right people near
          you — service providers, merchants, recruiters and neighbours — then carries
          the job from first message to final agreement.
        </SectionTitle>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {[
            {
              t: "Matched by town, not by ads",
              d: "Your town comes first. Matching starts where you live and widens only as far as it needs to — with identity checks and quality ratings for every provider.",
            },
            {
              t: "Communities you build",
              d: "Go-lists are your own circles of trusted providers. Blast a request to your list, or to the whole town, and let the right people respond.",
            },
            {
              t: "Negotiate to a real agreement",
              d: "The Bargaining Tab (B-Tab) carries quotes, tours and terms into one REQ — the single, up-to-date agreement both sides proceed on.",
            },
          ].map((s) => (
            <div key={s.t} className="border border-neutral-200 rounded-2xl p-6">
              <h3 className="font-semibold text-lg text-[#161A1D]">{s.t}</h3>
              <p className="text-neutral-600 mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ixee */}
      <section id="ixee" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="Ixee — the assistant" title="Ixee — just say the word.">
          Ixee is the AI assistant inside Goollooper, and inside Ixra. Talk to it by
          text, voice-to-text or voice, and it works on your behalf: finding providers,
          creating tasks, drafting REQ-invoices and following up — asking your approval
          wherever it matters. Ixee is an Immersive Mix of Everything Essential.
        </SectionTitle>
        <div className="mt-8">
          <a
            href="https://ixee.ai"
            className="border border-neutral-300 text-[#161A1D] font-medium px-6 py-3 rounded-full hover:border-neutral-500 inline-block"
          >
            Visit ixee.ai
          </a>
        </div>
      </section>

      {/* Ixra */}
      <section id="ixra" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="Ixra — immersive experience" title="A virtual-real anytime experience">
          Ixra lets a host share a real space with guests anywhere: capture a room with
          a simple phone, and guests walk through it in 3D, ask questions, and scope
          the work together — live in a headset, or as a flat video anyone can watch
          and quote from. No one is priced out of giving a quote.
        </SectionTitle>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          <div className="border border-neutral-200 rounded-2xl p-6">
            <h3
              className="font-semibold text-xl text-[#161A1D]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Scoping is Ixra's first job, not its only one.
            </h3>
            <p className="text-neutral-600 mt-2 leading-relaxed">
              A contractor who can see the room, the fittings and the measurements
              gives a better quote — without the drive, the scheduling dance, or the
              surprise on arrival.
            </p>
          </div>
          <div className="border border-neutral-200 rounded-2xl p-6">
            <h3
              className="font-semibold text-xl text-[#161A1D]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Live when it matters. Anytime when it doesn't.
            </h3>
            <p className="text-neutral-600 mt-2 leading-relaxed">
              Walk the space together in real time when the conversation needs it —
              or send the tour and let guests explore on their own schedule.
            </p>
          </div>
        </div>
        <p className="text-sm text-neutral-400 mt-6">In development for Meta Quest.</p>
      </section>

      {/* Whixpar */}
      <section id="whixpar" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="Whixpar — agent access" title="Delegation you stay in charge of">
          Whixpar is the layer that lets AI agents act on your behalf — in your chats,
          with your tools — without handing over the keys. Every delegation is scoped
          to a task, every action is checked against a risk ladder, and you keep the
          audit trail and the kill switch.
        </SectionTitle>
        <div className="flex flex-wrap gap-2 mt-8">
          {[
            "Scoped delegation",
            "Host-sovereign tool access",
            "Risk-ladder checks",
            "Full audit trail",
            "Kill switch",
          ].map((chip) => (
            <span
              key={chip}
              className="border border-neutral-300 rounded-full px-4 py-1.5 text-sm text-neutral-700"
            >
              {chip}
            </span>
          ))}
        </div>
        <p className="text-sm text-neutral-400 mt-6">In design.</p>
      </section>

      {/* Mega Blast */}
      <section id="megablast" className="max-w-6xl mx-auto px-5 py-16">
        <SectionTitle eyebrow="Mega Blast — the volunteer lane" title="Help, freely given">
          Not every connection is a transaction. With Mega Blast, members volunteer
          their time and skills to one another — matched by town and by interest,
          altruistic requests find altruistic hands. It is the platform's charitable
          lane, built into the same community that hires and helps every day.
        </SectionTitle>
      </section>

      {/* App CTA */}
      <section className="max-w-6xl mx-auto px-5 py-16 text-center">
        <h2
          className="text-3xl md:text-4xl font-bold text-[#161A1D]"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Start with the app.
        </h2>
        <p className="text-neutral-600 mt-4 max-w-xl mx-auto leading-relaxed">
          Goollooper is live on iOS and Android — and Ixee is already inside,
          ready when you are.
        </p>
        <div className="mt-8">
          <StoreButtons center />
        </div>
      </section>
    </main>
    <GoollooperFooter />
  </div>
);

export default GoollooperHome;
